import { useCallback, useEffect, useRef, useState } from 'react';
import { plataforma } from '@/platform';
import { HttpError } from '@/shared/api/httpClient';
import { avaliacoesApi, type AvaliacaoOS } from '@/features/pos-venda/api/avaliacoesApi';

type Estado = {
  avaliacao: AvaliacaoOS | null;
  /** Primeira carga ainda em andamento (recargas silenciosas não contam). */
  carregando: boolean;
  /** Servidor inalcançável — o card mostra um aviso discreto, a O.S. segue. */
  semConexao: boolean;
  /** Outro erro (ex.: rota ainda não publicada no servidor). */
  erro: string | null;
};

/** 503/Indisponivel (desktop offline ou servidor fora) = sem conexão. */
function ehSemConexao(e: unknown): boolean {
  if (e instanceof HttpError) {
    return e.status === 503 || e.payload?.tipo === 'Indisponivel';
  }
  return true;
}

/**
 * Avaliação do cliente para uma O.S. Os dados moram no servidor: no
 * desktop o IPC faz proxy, no web vai direto. Recarrega quando o servidor
 * avisa que o cliente respondeu (`posvenda.avaliacao`) e quando a janela
 * volta ao foco — o que cobrir o caso de o evento não chegar.
 */
export function useAvaliacaoOS(orcamentoId: string) {
  const [estado, setEstado] = useState<Estado>({
    avaliacao: null,
    carregando: true,
    semConexao: false,
    erro: null,
  });
  // Evita aplicar resposta de uma O.S. anterior se o id mudou no meio.
  const idAtual = useRef(orcamentoId);
  idAtual.current = orcamentoId;

  const recarregar = useCallback(async () => {
    const alvo = orcamentoId;
    try {
      const { avaliacao } = await avaliacoesApi.daOS(alvo);
      if (idAtual.current !== alvo) return;
      setEstado({ avaliacao, carregando: false, semConexao: false, erro: null });
    } catch (e) {
      if (idAtual.current !== alvo) return;
      const semConexao = ehSemConexao(e);
      setEstado((s) => ({
        // Mantém o último valor conhecido: perder a conexão não apaga a nota.
        avaliacao: s.avaliacao,
        carregando: false,
        semConexao,
        erro: semConexao ? null : e instanceof Error ? e.message : 'Falha ao carregar a avaliação.',
      }));
    }
  }, [orcamentoId]);

  useEffect(() => {
    setEstado({ avaliacao: null, carregando: true, semConexao: false, erro: null });
    void recarregar();
  }, [recarregar]);

  // Evento do servidor: o cliente acabou de responder.
  useEffect(
    () =>
      plataforma.aoAvaliacaoPosVenda((id) => {
        if (id === null || id === orcamentoId) void recarregar();
      }),
    [orcamentoId, recarregar],
  );

  // Fallback: ao voltar para a janela/aba, confere de novo.
  useEffect(() => {
    const aoFocar = () => {
      if (document.visibilityState === 'visible') void recarregar();
    };
    window.addEventListener('focus', aoFocar);
    document.addEventListener('visibilitychange', aoFocar);
    return () => {
      window.removeEventListener('focus', aoFocar);
      document.removeEventListener('visibilitychange', aoFocar);
    };
  }, [recarregar]);

  /** Gera (ou reaproveita) o link e já recarrega o card. */
  const gerarLink = useCallback(async (): Promise<string> => {
    const { link } = await avaliacoesApi.gerarLink(orcamentoId);
    void recarregar();
    return link;
  }, [orcamentoId, recarregar]);

  return { ...estado, recarregar, gerarLink };
}
